import React, { useEffect, useState } from "react";
import { Modal, Input, message } from "antd";
import { Copy, Check, Loader2 } from "lucide-react";
import { mfaApi, MfaSetupResponse } from "../../../api/mfa";
import { MFA_STATUS_CHANGED_EVENT } from "../../../components/Layout/MfaNudgeBanner";
import BackupCodesDisplay from "./BackupCodesDisplay";

interface MfaSetupModalProps {
  open: boolean;
  userId: string;
  onClose: () => void;
  onEnabled: () => void;
}

type Step = "scan" | "verify" | "backup-codes";

const MfaSetupModal: React.FC<MfaSetupModalProps> = ({
  open,
  userId,
  onClose,
  onEnabled,
}) => {
  const [step, setStep] = useState<Step>("scan");
  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [copied, setCopied] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setStep("scan");
    setCode("");
    setSetupData(null);
    setLoadingSetup(true);
    mfaApi
      .setup(userId)
      .then((res) => setSetupData(res.data))
      .catch(() => {
        message.error("Failed to start MFA setup");
        onClose();
      })
      .finally(() => setLoadingSetup(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const handleCopy = () => {
    if (!setupData) return;
    navigator.clipboard.writeText(setupData.manualEntryKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const res = await mfaApi.enable(userId, code);
      setBackupCodes(res.data.backupCodes);
      setStep("backup-codes");
    } catch (error) {
      message.error("Incorrect code, try again");
    } finally {
      setVerifying(false);
    }
  };

  const handleDone = () => {
    message.success("Two-factor authentication enabled");
    window.dispatchEvent(new Event(MFA_STATUS_CHANGED_EVENT));
    onEnabled();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      centered
      maskClosable={false}
    >
      <div className="py-2">
        {step === "scan" && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Scan QR code</h2>
            <p className="text-sm text-gray-600">
              Scan this code with your authenticator app (Google Authenticator,
              Authy, 1Password, etc.)
            </p>
            {loadingSetup || !setupData ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <img
                  src={setupData.qrCodeDataUri}
                  alt="MFA QR code"
                  className="mx-auto"
                />
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    Can't scan? Enter this key manually:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm break-all">
                      {setupData.manualEntryKey}
                    </code>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      {copied ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setStep("verify")}
                  className="w-full px-4 py-2 rounded-lg font-medium bg-primary hover:bg-primary-dark text-white transition-colors"
                >
                  Next
                </button>
              </>
            )}
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Enter the code</h2>
            <p className="text-sm text-gray-600">
              Enter the 6-digit code shown in your authenticator app.
            </p>
            <div className="flex justify-center">
              <Input.OTP length={6} value={code} onChange={setCode} />
            </div>
            <button
              type="button"
              onClick={handleVerify}
              disabled={code.length !== 6 || verifying}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium bg-primary hover:bg-primary-dark text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify"
              )}
            </button>
          </div>
        )}

        {step === "backup-codes" && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Save your backup codes
            </h2>
            <BackupCodesDisplay codes={backupCodes} onDone={handleDone} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MfaSetupModal;
